import { useState } from 'react'
import { Link } from 'react-router-dom'

import type { EvidenzeConformita, Nis2Report } from '../api/types'
import { localeCorrente, t } from '../i18n/lingua'
import { CRITICALITY_LABEL } from '../pages/Services'
import { Card } from './common'
import { DataTable, type Column } from './DataTable'

/**
 * The compliance report beyond the per-asset controls: the tenant-wide
 * evidence behind them (certificates, clear-text traffic, third-party
 * libraries, monitoring) and, for DORA, the critical or important functions
 * with what supports them (art. 8) and the ICT third parties with their
 * concentration (artt. 28–29). See conformita_service and dora_service.
 */

type Tono = 'ok' | 'warn' | 'error' | 'na'

/** One number of an area; `dettaglio` names the list that opens under it. */
interface Tessera {
  id: string
  valore: number | string
  etichetta: string
  tono?: Tono
  dettaglio?: string
}

interface Area {
  id: string
  titolo: string
  articolo: string
  tono: Tono
  sintesi: string
  tessere: Tessera[]
  pagina?: { a: string; etichetta: string }
}

const ETICHETTA_TONO: Record<Tono, () => string> = {
  ok: () => t('in regola'),
  warn: () => t('da sistemare'),
  error: () => t('critico'),
  na: () => t('non misurato'),
}

const CLASSE_TONO: Record<Tono, string> = {
  ok: 'badge badge--ok',
  warn: 'badge badge--warn',
  error: 'badge badge--error',
  na: 'badge badge--muted',
}

/** The articles each area answers to, per framework. */
const ARTICOLI: Record<string, Record<string, string>> = {
  nis2: { transito: 'art. 21 c.2 h', riposo: 'art. 21 c.2 h', terze: 'art. 21 c.2 d–e', monitoraggio: 'art. 21 c.2 b' },
  dora: { transito: 'art. 9 c.2', riposo: 'art. 9 c.2', terze: 'art. 9 c.4 f · art. 28', monitoraggio: 'art. 10 c.1' },
}

const LinkAsset = ({ id, nome }: { id: string; nome: string }) => <Link to={`/cis/${id}`}>{nome}</Link>

/**
 * The evidence behind the controls, area by area: each area says what it
 * answers to, whether it is in order, and every number opens — right below
 * it — the list of assets it is made of, with the page where the work is done.
 */
export function EvidenzePiattaforma({ ev, framework }: { ev: EvidenzeConformita; framework: string }) {
  const [aperta, setAperta] = useState<string | null>(null)
  const art = ARTICOLI[framework] ?? ARTICOLI.nis2
  const c = ev.certificati
  const m = ev.monitoraggio
  const r = ev.dati_a_riposo
  const d = ev.dettagli
  const nonMonitorati = m.asset_importanti - m.asset_importanti_monitorati

  const aree: Area[] = [
    {
      id: 'transito',
      titolo: t('Cifratura dei dati in transito'),
      articolo: art.transito,
      tono: c.totali === 0 && ev.in_chiaro.servizi === 0 ? 'na'
        : c.scaduti > 0 || ev.in_chiaro.servizi > 0 ? 'error'
          : c.in_scadenza_30 > 0 || c.autofirmati > 0 ? 'warn' : 'ok',
      sintesi: t('Certificati TLS letti dagli agenti CENSO sui servizi esposti e servizi che altre macchine chiamano in chiaro.'),
      tessere: [
        { id: 'cert', valore: c.totali, etichetta: t('certificati TLS su {n} asset', { n: c.asset }) },
        { id: 'scaduti', valore: c.scaduti, etichetta: t('scaduti'), tono: c.scaduti ? 'error' : 'ok', dettaglio: 'scaduti' },
        { id: 'in_scadenza', valore: c.in_scadenza_30, etichetta: t('in scadenza entro 30 giorni'), tono: c.in_scadenza_30 ? 'warn' : 'ok', dettaglio: 'in_scadenza' },
        { id: 'autofirmati', valore: c.autofirmati, etichetta: t('autofirmati'), tono: c.autofirmati ? 'warn' : 'ok', dettaglio: 'autofirmati' },
        { id: 'chiaro', valore: ev.in_chiaro.servizi, etichetta: t('servizi chiamati in chiaro su {n} server', { n: ev.in_chiaro.server }), tono: ev.in_chiaro.servizi ? 'error' : 'ok', dettaglio: 'chiaro' },
      ],
      pagina: { a: '/encryption', etichetta: t('Apri Cifratura') },
    },
    {
      id: 'riposo',
      titolo: t('Cifratura dei dati a riposo'),
      articolo: art.riposo,
      tono: !r || !r.in_uso ? 'na'
        : r.non_protetti + r.scansione_non_protette > 0 ? 'error'
          : r.senza_chiave > 0 || r.windows_senza_dati > 0 ? 'warn' : 'ok',
      sintesi: t('Volumi di sistema delle macchine Windows protetti da BitLocker: in dettaglio dove c’è l’agente CENSO, dalla scansione WinRM della sonda altrove.'),
      tessere: r ? [
        { id: 'protetti', valore: r.protetti + r.scansione_protette, etichetta: t('macchine con il volume di sistema protetto'), tono: 'ok', dettaglio: 'protetti' },
        { id: 'non_protetti', valore: r.non_protetti + r.scansione_non_protette, etichetta: t('macchine con il volume di sistema NON protetto'), tono: r.non_protetti + r.scansione_non_protette ? 'error' : 'ok', dettaglio: 'non_protetti' },
        { id: 'senza_chiave', valore: r.senza_chiave, etichetta: t('protetti senza chiave di ripristino in CENSO'), tono: r.senza_chiave ? 'warn' : 'ok', dettaglio: 'senza_chiave' },
        { id: 'senza_dati', valore: r.windows_senza_dati, etichetta: t('macchine Windows senza informazioni'), tono: r.windows_senza_dati ? 'warn' : 'ok' },
      ] : [],
      pagina: { a: '/bitlocker', etichetta: t('Apri BitLocker') },
    },
    {
      id: 'terze',
      titolo: t('Componenti software di terze parti'),
      articolo: art.terze,
      tono: ev.sbom.asset === 0 ? 'na' : ev.sbom.sfruttate > 0 ? 'error' : ev.sbom.librerie_vulnerabili > 0 ? 'warn' : 'ok',
      sintesi: t('Librerie delle applicazioni dalla distinta dei componenti (SBOM) confrontate con gli avvisi di vulnerabilità.'),
      tessere: [
        { id: 'sbom', valore: ev.sbom.asset, etichetta: t('asset con distinta dei componenti (SBOM)') },
        { id: 'librerie', valore: ev.sbom.librerie_vulnerabili, etichetta: t('librerie vulnerabili su {n} asset', { n: ev.sbom.asset_con_librerie_vulnerabili }), tono: ev.sbom.librerie_vulnerabili ? 'warn' : 'ok', dettaglio: 'librerie' },
        { id: 'kev', valore: ev.sbom.sfruttate, etichetta: t('sfruttate attivamente (CISA KEV)'), tono: ev.sbom.sfruttate ? 'error' : 'ok', dettaglio: 'kev' },
      ],
      pagina: { a: '/software-map?vista=vulnerabili', etichetta: t('Apri Application Maintenance') },
    },
    {
      id: 'monitoraggio',
      titolo: t('Monitoraggio'),
      articolo: art.monitoraggio,
      tono: !m.in_uso ? 'na' : nonMonitorati > 0 ? 'error' : 'ok',
      sintesi: m.in_uso
        ? t('Asset che sostengono funzioni critiche o importanti (dai Business Service) sotto il monitoraggio di CENSO Eagle.')
        : t('CENSO Eagle non è in uso: il monitoraggio va dimostrato con lo strumento adottato.'),
      tessere: m.in_uso ? [
        { id: 'monitorati', valore: m.asset_monitorati, etichetta: t('asset monitorati da CENSO Eagle') },
        { id: 'importanti', valore: `${m.asset_importanti_monitorati} / ${m.asset_importanti}`, etichetta: t('asset critici o importanti monitorati'), tono: nonMonitorati ? 'warn' : 'ok', dettaglio: 'importanti' },
        { id: 'non_monitorati', valore: nonMonitorati, etichetta: t('asset critici o importanti NON monitorati'), tono: nonMonitorati ? 'error' : 'ok', dettaglio: 'non_monitorati' },
      ] : [],
      pagina: m.in_uso ? { a: '/eagle/cis', etichetta: t('Apri Eagle') } : undefined,
    },
  ]

  const conProblemi = aree.filter((a) => a.tono === 'error' || a.tono === 'warn').length
  const scegli = (area: string, tessera: Tessera) => {
    if (!tessera.dettaglio) return
    const chiave = `${area}:${tessera.dettaglio}`
    setAperta(aperta === chiave ? null : chiave)
  }

  return (
    <div className="evidenze">
      <div className="evidenze-sommario">
        <div>
          <strong>{t('Evidenze dalla piattaforma')}</strong>
          <p className="field__hint" style={{ margin: '2px 0 0' }}>
            {conProblemi
              ? t('{n} aree su {tot} chiedono un intervento. Clicca un numero per vedere gli asset che lo compongono.', { n: conProblemi, tot: aree.length })
              : t('Nessuna area chiede un intervento. Clicca un numero per vedere gli asset che lo compongono.')}
          </p>
        </div>
        <div className="evidenze-sommario__aree">
          {aree.map((a) => (
            <a key={a.id} href={`#evidenza-${a.id}`} className="evidenze-sommario__voce">
              {a.titolo} <span className={CLASSE_TONO[a.tono]}>{ETICHETTA_TONO[a.tono]()}</span>
            </a>
          ))}
        </div>
      </div>
      <div className="evidenze-griglia">
        {aree.map((a) => {
          const dettaglio = aperta?.startsWith(`${a.id}:`) ? aperta.slice(a.id.length + 1) : null
          return (
            <section key={a.id} id={`evidenza-${a.id}`} className={`evidenze-area evidenze-area--${a.tono}`}>
              <header className="evidenze-area__testa">
                <div>
                  <h3>{a.titolo}</h3>
                  <span className="mono text-muted evidenze-area__articolo">{a.articolo}</span>
                </div>
                <span className={CLASSE_TONO[a.tono]}>{ETICHETTA_TONO[a.tono]()}</span>
              </header>
              <p className="evidenze-area__sintesi">{a.sintesi}</p>
              {a.tessere.length > 0 && (
                <div className="evidenze-tessere">
                  {a.tessere.map((x) => {
                    const attiva = dettaglio === x.dettaglio
                    const contenuto = (
                      <>
                        <span className={`evidenze-tessera__valore evidenze-tessera__valore--${x.tono ?? 'na'}`}>{x.valore}</span>
                        <span className="evidenze-tessera__etichetta">{x.etichetta}</span>
                      </>
                    )
                    return x.dettaglio && d ? (
                      <button key={x.id} type="button" aria-expanded={attiva}
                        className={`evidenze-tessera evidenze-tessera--cliccabile${attiva ? ' evidenze-tessera--attiva' : ''}`}
                        onClick={() => scegli(a.id, x)} title={t('Mostra gli asset')}>
                        {contenuto}
                      </button>
                    ) : (
                      <div key={x.id} className="evidenze-tessera">{contenuto}</div>
                    )
                  })}
                </div>
              )}
              {dettaglio && d && (
                <div className="evidenze-dettaglio">
                  <DettaglioEvidenza chiave={dettaglio} d={d} />
                </div>
              )}
              {a.pagina && (
                <footer className="evidenze-area__piede">
                  <Link to={a.pagina.a}>{a.pagina.etichetta} →</Link>
                </footer>
              )}
            </section>
          )
        })}
      </div>
    </div>
  )
}

type Dettagli = NonNullable<EvidenzeConformita['dettagli']>

/** The assets behind one number, as a table (search, columns, CSV, pages). */
function DettaglioEvidenza({ chiave, d }: { chiave: string; d: Dettagli }) {
  if (['scaduti', 'in_scadenza', 'autofirmati'].includes(chiave)) {
    const problema = chiave === 'scaduti' ? 'scaduto' : chiave === 'in_scadenza' ? 'in_scadenza' : 'autofirmato'
    const righe = d.certificati.filter((x) => (problema === 'autofirmato' ? x.self_signed : x.problem === problema))
    const colonne: Column<Dettagli['certificati'][number]>[] = [
      { key: 'ci_name', header: t('Asset'), render: (x) => <LinkAsset id={x.ci_id} nome={x.ci_name} /> },
      { key: 'port', header: t('Porta'), type: 'number', mono: true },
      { key: 'cn', header: t('Nome nel certificato'), mono: true },
      { key: 'issuer', header: t('Emesso da'), hidden: true },
      { key: 'not_after', header: t('Scadenza'), type: 'date', value: (x) => x.not_after ?? '',
        render: (x) => (x.not_after ? new Date(x.not_after).toLocaleDateString(localeCorrente()) : '—') },
      { key: 'days_left', header: t('Giorni'), type: 'number', value: (x) => x.days_left ?? '' },
    ]
    return <DataTable rows={righe} columns={colonne} rowKey={(x, i) => `${x.ci_id}:${x.port}:${i}`}
      conteggio={{ nome: t('certificati') }} initialSort={{ key: 'days_left', dir: 'asc' }}
      emptyText={t('Nessun certificato in questo elenco.')} />
  }
  if (chiave === 'chiaro') {
    return <DataTable rows={d.in_chiaro} rowKey={(x) => x.ci_id} conteggio={{ nome: t('server') }} columns={[
      { key: 'ci_name', header: t('Server'), render: (x) => <LinkAsset id={x.ci_id} nome={x.ci_name} /> },
      { key: 'services', header: t('Servizi chiamati in chiaro'), value: (x) => x.services.join(', '),
        render: (x) => x.services.map((s) => <div key={s} className="mono"><small>{s}</small></div>) },
      { key: 'n', header: t('Servizi'), type: 'number', value: (x) => x.services.length },
    ]} initialSort={{ key: 'n', dir: 'desc' }} />
  }
  if (chiave === 'librerie' || chiave === 'kev') {
    const righe = chiave === 'kev' ? d.librerie.filter((x) => x.exploited > 0) : d.librerie
    return <DataTable rows={righe} rowKey={(x) => x.ci_id} conteggio={{ nome: t('asset') }}
      initialSort={{ key: chiave === 'kev' ? 'exploited' : 'libraries', dir: 'desc' }} columns={[
        { key: 'ci_name', header: t('Asset'), render: (x) => <LinkAsset id={x.ci_id} nome={x.ci_name} /> },
        { key: 'libraries', header: t('Librerie vulnerabili'), type: 'number' },
        { key: 'critical', header: t('Critiche'), type: 'number' },
        { key: 'exploited', header: t('Sfruttate (KEV)'), type: 'number' },
      ]} />
  }
  if (chiave === 'importanti' || chiave === 'non_monitorati') {
    const righe = chiave === 'non_monitorati' ? d.importanti.filter((x) => !x.monitored) : d.importanti
    return <DataTable rows={righe} rowKey={(x) => x.ci_id} conteggio={{ nome: t('asset') }} columns={[
      { key: 'ci_name', header: t('Asset'), render: (x) => <LinkAsset id={x.ci_id} nome={x.ci_name} /> },
      { key: 'criticality', header: t('Criticità'), value: (x) => (x.criticality ? CRITICALITY_LABEL[x.criticality] ?? x.criticality : '') },
      { key: 'monitored', header: t('Monitorato'), value: (x) => (x.monitored ? t('sì') : t('no')),
        render: (x) => <span className={x.monitored ? 'badge badge--ok' : 'badge badge--error'}>{x.monitored ? t('sì') : t('no')}</span> },
    ]} />
  }
  // Data at rest: the agent's volumes and the machines known only from the scan.
  type Riga = { ci_id: string; ci_name: string; mount: string; protected: boolean; escrowed: boolean | null; fonte: string; method: string }
  const righe: Riga[] = [
    ...d.volumi.map((v) => ({ ci_id: v.ci_id, ci_name: v.ci_name, mount: v.mount, protected: v.protection === 'On',
      escrowed: v.escrowed, fonte: t('agente CENSO'), method: v.method })),
    ...d.scansione.map((v) => ({ ci_id: v.ci_id, ci_name: v.ci_name, mount: '', protected: v.protected, escrowed: null,
      fonte: t('scansione WinRM'), method: '' })),
  ].filter((x) => (chiave === 'protetti' ? x.protected : chiave === 'non_protetti' ? !x.protected
    : x.protected && x.escrowed === false))
  return <DataTable rows={righe} rowKey={(x, i) => `${x.ci_id}:${x.mount}:${i}`} conteggio={{ nome: t('macchine') }} columns={[
    { key: 'ci_name', header: t('Macchina'), render: (x) => <LinkAsset id={x.ci_id} nome={x.ci_name} /> },
    { key: 'mount', header: t('Volume'), mono: true },
    { key: 'protected', header: t('Protezione BitLocker'), value: (x) => (x.protected ? t('attiva') : t('spenta')),
      render: (x) => <span className={x.protected ? 'badge badge--ok' : 'badge badge--error'}>{x.protected ? t('attiva') : t('spenta')}</span> },
    { key: 'method', header: t('Metodo'), mono: true },
    { key: 'escrowed', header: t('Chiave in CENSO'), value: (x) => (x.escrowed === null ? '' : x.escrowed ? t('sì') : t('no')) },
    { key: 'fonte', header: t('Fonte') },
  ]} />
}

const criticita = (c: string | null | undefined) => (c ? CRITICALITY_LABEL[c] ?? c : '—')

type AppCritica = NonNullable<Nis2Report['dora']>['funzioni_critiche']['apps'][number]
type FornitoreIct = NonNullable<Nis2Report['dora']>['fornitori_ict']['vendors'][number]

export function FunzioniCritiche({ dati }: { dati: NonNullable<Nis2Report['dora']>['funzioni_critiche'] }) {
  const colonneFunzioni: Column<AppCritica>[] = [
    {
      key: 'name',
      header: t('Business App'),
      value: (a) => `${a.name}${a.owner ? ` ${a.owner}` : ''}`,
      render: (a) => (
        <>
          <Link to={`/business-apps/${a.id}`}>{a.name}</Link>
          {a.owner && <><br /><small className="text-muted">{a.owner}</small></>}
        </>
      ),
    },
    { key: 'criticality', header: t('Criticità'), value: (a) => (a.criticality ? criticita(a.criticality) : '') },
    {
      key: 'functions',
      header: t('Funzioni'),
      value: (a) => a.functions.map((f) => `${f.name} · ${criticita(f.criticality)}`).join(', '),
      render: (a) =>
        a.functions.length === 0 ? (
          <span className="text-muted">—</span>
        ) : (
          a.functions.map((f) => <div key={f.name}><small>{f.name} · {criticita(f.criticality)}</small></div>)
        ),
    },
    { key: 'cis', header: t('Asset'), type: 'number' },
    { key: 'non_compliant', header: t('Non conformi'), type: 'number' },
    {
      key: 'critical_band',
      header: t('In fascia critica'),
      type: 'number',
      render: (a) => <span style={{ color: a.critical_band > 0 ? 'var(--error)' : undefined }}>{a.critical_band}</span>,
    },
  ]
  return (
    <Card title={t('Funzioni critiche o importanti (art. 8)')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Business App, funzioni associate, criticità dichiarata e asset ICT che le supportano, con evidenza degli asset conformi e non conformi. Gli asset dei Business Service critici o alti sono {n}, di cui {k} non conformi.', { n: dati.asset_importanti, k: dati.asset_importanti_non_conformi })}
      </p>
      {dati.apps.length === 0 ? (
        <p className="text-muted"><small>{t('Se non sono definite Business App, devono essere create dalla Mappa Software, selezionando i programmi che compongono ciascuna applicazione.')}</small></p>
      ) : (
        <DataTable
          rows={dati.apps}
          columns={colonneFunzioni}
          rowKey={(a) => a.id}
          conteggio={{ nome: t('applicazioni') }}
        />
      )}
    </Card>
  )
}

export function FornitoriIct({ dati }: { dati: NonNullable<Nis2Report['dora']>['fornitori_ict'] }) {
  const valuta = (v: number) => v.toLocaleString(localeCorrente(), { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
  const colonneFornitori: Column<FornitoreIct>[] = [
    {
      key: 'vendor',
      header: t('Fornitore'),
      render: (v) => (
        <>
          {v.supplier_id ? <Link to={`/suppliers/${v.supplier_id}`}>{v.vendor}</Link> : v.vendor}
          {v.source === 'inventario' && <><br /><small className="text-muted">{t('dall’inventario cloud')}</small></>}
          {v.source === 'contratto' && <><br /><small className="text-muted">{t('dal contratto, non ancora nel registro fornitori')}</small></>}
          {v.lei && <><br /><small className="text-muted mono">LEI {v.lei}</small></>}
        </>
      ),
    },
    {
      key: 'contracts',
      header: t('Contratti'),
      value: (v) => v.contracts.map((c) => c.reference).join(', '),
      render: (v) =>
        v.contracts.length === 0 ? (
          <span className="text-muted">—</span>
        ) : (
          v.contracts.map((c) => (
            <div key={c.id}>
              <small>
                <Link to={`/contracts/${c.id}`}>{c.reference}</Link>
                {c.end_date ? ` · ${t('scade il {d}', { d: c.end_date })}` : ''}
              </small>
            </div>
          ))
        ),
    },
    { key: 'cis', header: t('Asset'), type: 'number' },
    { key: 'critical_cis', header: t('Asset critici'), type: 'number' },
    {
      key: 'concentration',
      header: t('Concentrazione'),
      type: 'number',
      render: (v) => (
        <span style={{ color: (v.concentration ?? 0) >= 50 ? 'var(--warn)' : undefined }}>
          {v.concentration == null ? '—' : `${v.concentration}%`}
        </span>
      ),
    },
    {
      key: 'annual_value',
      header: t('Valore annuo'),
      type: 'number',
      value: (v) => v.annual_value || null,
      render: (v) => (v.annual_value ? valuta(v.annual_value) : '—'),
    },
    {
      key: 'issues',
      header: t('Da sistemare'),
      value: (v) => v.issues.join(', '),
      render: (v) =>
        v.issues.length === 0 ? (
          <span className="text-muted">—</span>
        ) : (
          v.issues.map((i) => <div key={i}><small>{i}</small></div>)
        ),
    },
  ]
  return (
    <Card title={t('Fornitori ICT terzi e concentrazione (artt. 28–29)')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('I fornitori ICT del registro fornitori (dichiarati, o di tipo cloud e servizi ICT) e quelli con contratti attivi, con gli asset coperti e i provider cloud che ospitano l’inventario. La «concentrazione» è la quota di asset a supporto di funzioni critiche o importanti che dipende da ciascun fornitore. Per chi sostiene funzioni critiche o importanti il registro segnala LEI, sostituibilità e strategia di uscita mancanti. L’esportazione del registro delle informazioni è nell’area Fornitori.')}
      </p>
      <p style={{ margin: '4px 0 8px' }}>
        <small>
          {t('{c} contratti attivi', { c: dati.contracts })} · {t('{n} fornitori senza contratto registrato', { n: dati.without_contract })} · {t('concentrazione massima {p}%', { p: dati.max_concentration })}
        </small>
      </p>
      {dati.vendors.length === 0 ? (
        <p className="text-muted"><small>{t('Nessun contratto registrato e nessun provider cloud nell’inventario.')}</small></p>
      ) : (
        <DataTable
          rows={dati.vendors}
          columns={colonneFornitori}
          rowKey={(v) => v.vendor}
          conteggio={{ nome: t('fornitori') }}
        />
      )}
    </Card>
  )
}
